/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/admin/RoleGuard.tsx
 *
 * Rôle :
 *   Wrapper React qui protège les routes admin. Lit le rôle courant via
 *   `GET /api/me/role.php`, et :
 *     - tant que le fetch est en cours → écran de vérification (skeleton),
 *     - si non authentifié → redirige vers `/admin/login` en gardant
 *       l'URL de retour (`?from=`),
 *     - si authentifié mais mauvais rôle → message accessible « Accès
 *       refusé » + bouton retour,
 *     - si rôle OK → rend les children.
 *
 *   Cette protection est purement UI, la vraie sécurité reste serveur
 *   (auth_check_admin sur chaque endpoint admin). Réponse au risque
 *   résiduel S7 (RoleGuard front absent).
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE, utilisable partout où on a un endpoint
 *   `/api/me/role.php` qui renvoie `{ authenticated, kind, role }`.
 *
 * @author Mika
 * @since  2026-05-05
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { apiRequest, ApiError } from '@/api/client';
import { impersonationLandingPath } from '@/api/impersonation';
import { Button, Card } from '@/components/ui';
import styles from './RoleGuard.module.css';

/**
 * Réponse de /api/me/role.php, superset des trois cas.
 * Volontairement permissif (any-cast nul, typage discriminé minimal).
 */
export interface MeRoleResponse {
  ok: true;
  authenticated: boolean;
  kind: 'admin' | 'visitor' | 'anon';
  role: string | null;
  admin_id?: number;
  user_id?: number;
  totp_verified?: boolean;
  permissions?: Record<string, string[]>;
  csrf_token?: string;
  first_name?: string;
  last_name?: string;
  reason?: string;
  degraded?: boolean;
  /**
   * Bloc envoye par me/role.php quand un emprunt d'identite est actif.
   * Ignore jusqu'au 2026-09-11 : voir la decision `redirect_emprunt`.
   */
  impersonation?: {
    active?: boolean;
    target_role?: string | null;
    target_is_admin?: boolean;
    target_label?: string;
  } | null;
}

export interface RoleGuardProps {
  /**
   * Liste des rôles autorisés. Si la liste est vide, on accepte toute
   * session admin authentifiée. Cas spécial : `['*authenticated']` accepte
   * tout user avec `authenticated=true` indépendamment du rôle.
   */
  requiredRoles: string[];
  /** Contenu protégé. */
  children: ReactNode;
  /**
   * URL de redirection si non authentifié. Défaut : `/admin/login`.
   */
  loginPath?: string;
  /**
   * Si `true`, n'affiche pas l'écran « Accès refusé » et redirige
   * silencieusement vers `loginPath`.
   */
  silentDeny?: boolean;
}

/**
 * Composant de garde côté front. À utiliser dans un `<Route element={…}>`
 * pour wrapper un sous-arbre admin.
 */
export function RoleGuard({
  requiredRoles,
  children,
  loginPath = '/admin/login',
  silentDeny = false,
}: RoleGuardProps) {
  const intl = useIntl();
  const location = useLocation();

  const query = useQuery<MeRoleResponse>({
    queryKey: ['me', 'role'],
    queryFn: async () => apiRequest<MeRoleResponse>('me/role.php'),
    // Le rôle est stable sur la durée d'une session, pas de polling agressif.
    staleTime: 60_000,
    // En cas d'erreur (5xx réseau), on ne déclenche pas un refetch en boucle.
    retry: 1,
  });

  // Calcule l'autorisation finale.
  const decision = useMemo<
    'loading' | 'allow' | 'redirect_login' | 'deny_role' | 'server_error' | 'redirect_emprunt'
  >(() => {
    if (query.isLoading) return 'loading';
    if (query.isError) {
      // FIX 2026-05-18 (bug Mika « je tourne en rond ») : CORRECTIF
      // STRUCTUREL DÉFINITIF de la boucle de connexion.
      // me/role.php renvoie 200 + {authenticated:false} pour un
      // anonyme (PAS une erreur). Donc query.isError ne survient QUE
      // sur 401/403 (session invalide) OU 5xx/réseau (serveur HS).
      //  - 401/403 → redirige vers /admin/login (légitime).
      //  - 5xx / réseau / autre → ÉCRAN D'ERREUR (pas de redirection !).
      // Avant ce fix : TOUTE erreur → redirect login → l'admin se
      // reconnecte → me/role.php re-échoue (500) → redirect login →
      // BOUCLE INFINIE. Se reconnecter ne corrige jamais un 500 :
      // il ne FAUT pas renvoyer au login dans ce cas.
      const err = query.error;
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return 'redirect_login';
      }
      return 'server_error';
    }
    const data = query.data;

    // UN EMPRUNT D'IDENTITE N'EST PAS UNE SESSION PERDUE.
    //
    // Mesure le 2026-09-11 : pendant l'emprunt d'un compte participant, le
    // back-office est ferme PAR CONCEPTION et me/role.php repond avec le role
    // de la cible. Cette garde lisait « exposant », refusait, et selon la
    // page renvoyait a l'ecran de connexion. La personne en concluait que sa
    // session avait expire, se reconnectait avec sa double authentification,
    // ce qui effacait l'emprunt, et recommencait. Trois fois en sept minutes
    // dans le journal d'audit, sans jamais un seul `stop`.
    //
    // La session n'avait jamais expire. La bonne sortie est « Revenir a mon
    // compte », que seul le bandeau propose, et le bandeau ne s'affiche que
    // dans l'espace emprunte. On y renvoie donc, au lieu de montrer une page
    // de connexion qui ment. Mikael - chef de projet - 2026-09-11
    if (data?.impersonation?.active === true && data.impersonation.target_is_admin !== true) {
      return 'redirect_emprunt';
    }

    if (!data || !data.authenticated || !data.role) {
      // FIX 2026-08-25 (bug prod « je tourne en rond », defense en
      // profondeur), un « anonyme » MIS EN CACHE puis en cours de
      // rafraichissement ne doit pas decider d'une redirection : la session
      // a pu etre ouverte entre-temps (connexion + 2FA), et react-query sert
      // la valeur perimee le temps du refetch. On attend la reponse fraiche.
      if (query.isFetching) return 'loading';
      return 'redirect_login';
    }
    if (requiredRoles.length === 0 || requiredRoles.includes('*authenticated')) {
      return 'allow';
    }
    return requiredRoles.includes(data.role) ? 'allow' : 'deny_role';
  }, [query.data, query.error, query.isError, query.isFetching, query.isLoading, requiredRoles]);

  if (decision === 'loading') {
    return (
      <div
        className={styles.container}
        role="status"
        aria-live="polite"
        aria-label={intl.formatMessage({ id: 'roleGuard.loading.aria' })}
      >
        <div className={styles.skeleton} aria-hidden="true" />
        <p className={styles.loadingText}>
          <FormattedMessage id="roleGuard.loading" />
        </p>
      </div>
    );
  }

  // FIX 2026-05-18, serveur HS (5xx/réseau) : on N'ENVOIE PAS au
  // login (sinon boucle infinie). Écran d'erreur + bouton réessayer.
  if (decision === 'server_error') {
    return (
      <main className={styles.container}>
        <Card variant="default" className={styles.deniedCard}>
          <h1 className={styles.deniedTitle}>
            <FormattedMessage id="roleGuard.serverError.title" />
          </h1>
          <p className={styles.deniedBody}>
            <FormattedMessage id="roleGuard.serverError.body" />
          </p>
          <div className={styles.deniedActions}>
            <Button
              variant="primary"
              size="md"
              onClick={() => void query.refetch()}
            >
              <FormattedMessage id="roleGuard.serverError.retry" />
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => {
                window.location.href = loginPath;
              }}
            >
              <FormattedMessage id="roleGuard.serverError.toLogin" />
            </Button>
          </div>
        </Card>
      </main>
    );
  }

  if (decision === 'redirect_emprunt') {
    return <Navigate to={impersonationLandingPath(query.data?.impersonation?.target_role)} replace />;
  }

  if (decision === 'redirect_login') {
    const from = location.pathname + location.search;
    return <Navigate to={`${loginPath}?from=${encodeURIComponent(from)}`} replace />;
  }

  if (decision === 'deny_role') {
    if (silentDeny) {
      return <Navigate to={loginPath} replace />;
    }
    return (
      <main className={styles.container}>
        <Card variant="default" className={styles.deniedCard}>
          <h1 className={styles.deniedTitle}>
            <FormattedMessage id="roleGuard.denied.title" />
          </h1>
          <p className={styles.deniedBody}>
            <FormattedMessage
              id="roleGuard.denied.body"
              values={{ role: query.data?.role ?? '' }}
            />
          </p>
          <p className={styles.deniedHint}>
            <FormattedMessage id="roleGuard.denied.hint" />
          </p>
          <div className={styles.deniedActions}>
            <Button
              variant="secondary"
              size="md"
              onClick={() => window.history.back()}
            >
              <FormattedMessage id="btn.back" />
            </Button>
          </div>
        </Card>
      </main>
    );
  }

  // decision === 'allow'
  return <>{children}</>;
}

export default RoleGuard;

// ──────────────────────────────────────────────────────────────────────────
// Notes Mika (S12) :
//  - On ne stocke PAS le rôle dans un store Zustand local : le source of
//    truth reste le serveur. TanStack Query gère le cache (staleTime 60 s)
//    et la dédup. Si un autre composant a besoin du rôle, il refait
//    `useQuery(['me','role'])` → cache hit immédiat.
//  - Réponse au risque résiduel S7 : « RoleGuard front absent ».
//  - Compat WCAG : `role="status"` + `aria-live="polite"` pour l'écran
//    de chargement, lecteurs d'écran annoncent la vérification.
// ──────────────────────────────────────────────────────────────────────────
